import { createSlice, createAsyncThunk, isAnyOf } from '@reduxjs/toolkit'
import lang from '@/lang'
import FileService from '@/modules/file/fileService'

// FLAG:docuAiEnhancements — "Meine Meldungen": the customer-facing list of the tenant's
// reported analysis/upload problems and their coarse state + the polite messages
// sent to the customer. Read straight from the DMS public API via FileService;
// the DMS returns ONLY the customer-safe projection (never the internal engine
// status/source/findings).

// The five coarse customer states — the German strings ARE the wire values (contract:
// dms-s3 CUSTOMER_ISSUE_STATUS enum; see REPO_INTERACTION.md "User-reported analysis problems").
// Unknown values must still render (neutral chip), never break — forward compatibility.
// Defined in modules/customer-issues/status.ts (lang-only) and re-exported here, so the display
// and list configuration never import this duck at runtime — that pulled FileService → store →
// lists duck into a cycle and crashed the app on start (2026-09-21).
export { CUSTOMER_ISSUE_STATUSES } from '@/modules/customer-issues/status'
export type { CustomerIssueStatus } from '@/modules/customer-issues/status'

export interface CustomerIssueMessage {
  body: string
  at: string
  // The coarse customerStatus at the time the message was written (optional).
  status?: string
}

export interface CustomerIssue {
  id: string
  versionId: string
  minioId: string
  title: string | null
  description: string
  // One of CUSTOMER_ISSUE_STATUSES; typed string because the wire is not enforced —
  // render unknown values neutrally instead of trusting the union blindly.
  customerStatus: string
  customerMessage: string | null
  messages: CustomerIssueMessage[]
  createdAt: string
  updatedAt: string
}

export interface CustomerIssuesState {
  items: CustomerIssue[]
  /**
   * pagination.total of the DMS response — how many issues the tenant has in TOTAL. The slice
   * fetches only the newest CUSTOMER_ISSUES_PAGE_LIMIT, so items.length < total means the list is
   * truncated and must say so instead of silently swallowing older rows (the DMS cap
   * MELDUNGEN_MAX_OPEN_PER_TENANT limits only OPEN issues — resolved/closed ones accumulate).
   */
  total: number
  /**
   * Die aktuell angezeigte Seite, 1-basiert. Bis 2026-09-21 gab es das nicht: die Liste holte
   * fest `page: 1` und wies mit einem Satz darauf hin, dass aeltere Meldungen abgeschnitten
   * sind. Es gab damit keinen Weg zu einer Meldung ausserhalb der ersten Seite.
   */
  page: number
  isLoading: boolean
  error: string | null
  /**
   * Latest-request-wins: requestId of the LAST started fetchCustomerIssues. Mount, MELDUNG:UPDATED,
   * reconnect and window focus all dispatch the thunk and can overlap; only the answer of the
   * latest request may write the state, otherwise a slow older response overwrites a newer list.
   */
  latestRequestId: string | null
}

const initialState: CustomerIssuesState = {
  items: [],
  total: 0,
  page: 1,
  isLoading: false,
  error: null,
  latestRequestId: null,
}

/**
 * Zeilen pro Seite. War 100 und meinte „alles, was wir laden" — mit einem Hinweistext statt
 * Blaettern. Seit 2026-09-21 wird geblaettert, also ist das eine echte Seitengroesse. Die DMS-Seite
 * deckelt bei 100, dieser Wert muss also darunter bleiben.
 */
export const CUSTOMER_ISSUES_PAGE_LIMIT = 20

/**
 * Blockgroesse fuer fetchAllCustomerIssues — genau der Deckel der DMS-Seite
 * (dms-s3 `customer-issue.service.ts`: `Math.min(100, …)`). Groesser geht nicht, kleiner kostet Anfragen.
 */
export const CUSTOMER_ISSUES_FETCH_ALL_LIMIT = 100

/**
 * Harte Obergrenze fuer fetchAllCustomerIssues: hoechstens so viele Bloecke je Ladevorgang
 * (10 × 100 = 1000 Meldungen). Darueber bleibt die Liste bewusst abgeschnitten und sagt es
 * (lang.customerIssues.truncated) — ein unbegrenzter Faecher bei jedem Neuladen waere ein
 * Lastrisiko, nicht bloss langsam.
 */
export const CUSTOMER_ISSUES_FETCH_ALL_MAX_PAGES = 10

export interface CustomerIssuesPage {
  items: CustomerIssue[]
  total: number
  page: number
}

/**
 * Eine Seite Meldungen laden. Ohne Argument die aktuelle (bzw. die erste) — so bleiben die
 * bestehenden Aufrufer (Mount, MELDUNG:UPDATED, Reconnect, window.focus) unveraendert und
 * aktualisieren die Seite, auf der der Nutzer gerade steht, statt ihn auf Seite 1 zu werfen.
 */
export const fetchCustomerIssues = createAsyncThunk<CustomerIssuesPage, number | void, { rejectValue: string }>(
  'customerIssues/fetchCustomerIssues',
  async(page, { getState, rejectWithValue }) => {
    const state = getState() as { customerIssues: CustomerIssuesState }
    const requestedPage = Math.max(1, Number(page) || state.customerIssues.page || 1)
    try {
      const res = await FileService.listMyIssues({ page: requestedPage, limit: CUSTOMER_ISSUES_PAGE_LIMIT })
      const items = res?.data?.result ?? []
      // pagination.total is the wire contract (DMS listReportedProblems); fall back to
      // items.length so an older DMS without the field renders untruncated.
      return { items, total: res?.data?.pagination?.total ?? items.length, page: requestedPage }
    } catch {
      return rejectWithValue(lang.customerIssues.loadError)
    }
  },
)

/**
 * ALLE Meldungen des Mandanten laden, in Bloecken von CUSTOMER_ISSUES_FETCH_ALL_LIMIT.
 *
 * Fuer die list-v2-Liste (seit 2026-09-21): Suche, Statusfilter, Sortierung und Blaettern
 * macht dort ag-grid clientseitig — der DMS-Endpunkt kennt nur `page`/`limit` und sortiert fest
 * nach createdAt DESC. Eine einzelne Seite waere fuer den Client-Filter falsch (er saehe nur die
 * geladenen Zeilen), also holt dieser Thunk nach, bis `items.length >= total`. Eine leere Antwort
 * beendet die Schleife, statt endlos zu laufen.
 */
export const fetchAllCustomerIssues = createAsyncThunk<CustomerIssuesPage, void, { rejectValue: string }>(
  'customerIssues/fetchAllCustomerIssues',
  async(_, { rejectWithValue }) => {
    try {
      const items: CustomerIssue[] = []
      const seen = new Set<string>()
      let total = 0
      for (let page = 1; page <= CUSTOMER_ISSUES_FETCH_ALL_MAX_PAGES; page += 1) {
        const res = await FileService.listMyIssues({ page, limit: CUSTOMER_ISSUES_FETCH_ALL_LIMIT })
        const chunk: CustomerIssue[] = res?.data?.result ?? []
        // Seiten koennen sich ueberlappen, wenn zwischen zwei Anfragen eine neue Meldung oben
        // einsortiert wird (createdAt DESC verschiebt die Grenzen) — eine id zaehlt nur einmal,
        // sonst kollidiert ag-grid mit getRowId und die Zaehler stimmen nicht.
        for (const issue of chunk) {
          if (seen.has(issue.id)) { continue }
          seen.add(issue.id)
          items.push(issue)
        }
        total = res?.data?.pagination?.total ?? items.length
        if (chunk.length === 0 || items.length >= total) { break }
      }
      return { items, total, page: 1 }
    } catch {
      return rejectWithValue(lang.customerIssues.loadError)
    }
  },
)

const customerIssuesSlice = createSlice({
  name: 'customerIssues',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // Seitenweises Laden (fetchCustomerIssues) und das Laden aller Meldungen fuer die list-v2-Liste
    // (fetchAllCustomerIssues, seit 2026-09-21) teilen Zustand und Latest-request-wins-Regel.
    builder
      .addMatcher(isAnyOf(fetchCustomerIssues.pending, fetchAllCustomerIssues.pending), (state, action) => {
        state.isLoading = true
        state.error = null
        state.latestRequestId = action.meta.requestId
      })
      .addMatcher(isAnyOf(fetchCustomerIssues.fulfilled, fetchAllCustomerIssues.fulfilled), (state, action) => {
        // A late answer of an outdated request must not overwrite the newer list.
        if (action.meta.requestId !== state.latestRequestId) { return }
        state.isLoading = false
        state.items = action.payload.items
        state.total = action.payload.total
        // Die Seite wird ERST hier gesetzt, nicht beim Anfordern: schlaegt der Abruf fehl,
        // zeigt die Paginierung weiter die Seite, deren Inhalt tatsaechlich dasteht.
        state.page = action.payload.page
      })
      .addMatcher(isAnyOf(fetchCustomerIssues.rejected, fetchAllCustomerIssues.rejected), (state, action) => {
        if (action.meta.requestId !== state.latestRequestId) { return }
        state.isLoading = false
        state.error = action.payload ?? lang.customerIssues.loadError
      })
  },
})

export default customerIssuesSlice.reducer
